import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Bolt, Crown, Diamond, Drum, Flame, Headphones, Heart, KeyboardMusic,
  Mic2, Moon, Music, Rocket, Smile, Star, Sun, Waves,
} from 'lucide-react';
import {
  claimSeatPictures, joinNewSeat, loadSeatSession, lookupClass, repickSeat,
  SEAT_TOKEN_KEY, signInSeat, signInWithBackup,
} from '@/api/seat';
import { normalizeSeatDisplayName, purgeSeatBackups } from '@/state/seatSession';
import { useNoReferrer } from './useNoReferrer';

export const PICTURE_CHOICES = [
  ['drum', 'Drum', Drum, 'var(--ba-class-drums)'],
  ['keys', 'Keys', KeyboardMusic, 'var(--ba-class-melodies)'],
  ['mic', 'Mic', Mic2, 'var(--ba-class-vocals)'],
  ['headphones', 'Headphones', Headphones, 'var(--ba-solo)'],
  ['star', 'Star', Star, 'var(--ba-class-sound-effects)'],
  ['fire', 'Fire', Flame, 'var(--ba-record)'],
  ['moon', 'Moon', Moon, 'var(--ba-class-textures)'],
  ['bolt', 'Bolt', Bolt, 'var(--ba-solo)'],
  ['crown', 'Crown', Crown, 'var(--ba-class-sound-effects)'],
  ['rocket', 'Rocket', Rocket, 'var(--ba-class-melodies)'],
  ['heart', 'Heart', Heart, 'var(--ba-record)'],
  ['wave', 'Wave', Waves, 'var(--ba-class-textures)'],
  ['music', 'Music', Music, 'var(--ba-class-vocals)'],
  ['sun', 'Sun', Sun, 'var(--ba-solo)'],
  ['diamond', 'Diamond', Diamond, 'var(--ba-class-melodies)'],
  ['smile', 'Smile', Smile, 'var(--ba-accent)'],
];

const QR_HASH = /(^#|&)(code|seat|key)=/;

/**
 * A seat-card QR opened in a tab that is already on /join only changes the URL fragment, which
 * does not reload the page. Call onQr so the route can restart sign-in and consume the fragment.
 */
export function useQrHashChange(onQr) {
  const latest = useRef(onQr);
  latest.current = onQr;
  useEffect(() => {
    const handle = () => { if (QR_HASH.test(window.location.hash)) latest.current(); };
    window.addEventListener('hashchange', handle);
    return () => window.removeEventListener('hashchange', handle);
  }, []);
}

export function consumeQrFragment(location = window.location, history = window.history) {
  if (!location.hash || location.hash.length < 2) return null;
  const values = new URLSearchParams(location.hash.slice(1));
  const code = values.get('code');
  const seat = Number(values.get('seat'));
  const key = values.get('key');
  if (values.has('code') || values.has('seat') || values.has('key')) {
    history.replaceState(history.state, '', `${location.pathname}${location.search}`);
  }
  if (!code || !Number.isInteger(seat) || seat < 1 || !key) return null;
  return { code, seatNumber: seat, backupCode: key };
}

function PictureGrid({ selected, onPick }) {
  return <>
    <div className="join-picture-slots" aria-label="Pictures picked">
      {[0, 1, 2].map((index) => <span key={index}>{selected[index]
        ? PICTURE_CHOICES.find(([id]) => id === selected[index])?.[1]
        : index + 1}</span>)}
    </div>
    <div className="join-picture-grid">
      {PICTURE_CHOICES.map(([id, label, Icon, color]) => <button
        key={id}
        type="button"
        style={{ '--picture-color': color }}
        aria-label={label}
        onClick={() => onPick(id)}
      ><Icon aria-hidden="true" /><span>{label}</span></button>)}
    </div>
  </>;
}

const classCode = (value) => value.toUpperCase().replace(/[^A-Z2-9-]/g, '').slice(0, 7);

export function JoinPage({ onSession, signedOutMessage = '' }) {
  useNoReferrer();
  const qr = useRef(consumeQrFragment());
  const queryCode = useMemo(() => new URLSearchParams(window.location.search).get('code') || '', []);
  const [step, setStep] = useState('boot');
  const [code, setCode] = useState(() => classCode(qr.current?.code || queryCode));
  const [program, setProgram] = useState(null);
  const [seats, setSeats] = useState([]);
  const [openSeats, setOpenSeats] = useState(0);
  const [seat, setSeat] = useState(null);
  const [name, setName] = useState('');
  const [secret, setSecret] = useState([]);
  const [firstSecret, setFirstSecret] = useState(null);
  const [seatToken, setSeatToken] = useState(null);
  const [message, setMessage] = useState(signedOutMessage);
  const [busy, setBusy] = useState(false);

  const openSession = async (token) => {
    const session = await loadSeatSession(token);
    sessionStorage.setItem(SEAT_TOKEN_KEY, token);
    if (session.mode === 'home') purgeSeatBackups();
    else purgeSeatBackups(sessionStorage, { seatId: session.seat_id, tokenVersion: session.token_version });
    onSession(session, token);
  };

  const showError = (error) => {
    if (error.code === 'program_ended' || error.status === 410) {
      setMessage('This class has ended. Tap your name to hear your beats.');
      setStep('grid');
    } else if (error.code === 'class_not_found' || error.status === 404) {
      setMessage('This class is no longer available. Check the code with your teacher.');
      setStep('code');
    } else if (error.code === 'name_taken') {
      setMessage('Someone in your class already has that name. Add another letter, like Jaylen Ro.');
      setStep('name');
    } else if (error.code === 'class_full') {
      setMessage('All seats are taken. Ask your teacher to add a seat.');
      setStep('grid');
    } else if (error.code === 'repick_unavailable') {
      setMessage('Ask your teacher to reset your pictures.');
      setStep('grid');
    } else if (error.code === 'seat_locked_staff') {
      setMessage('Ask your teacher to reset your pictures.');
    } else if (error.code === 'seat_locked') {
      setMessage('Seat locked for a few minutes. Ask your teacher.');
    } else if (error.code === 'slow_down') {
      setMessage('Please wait a moment, then try again.');
    } else if (error.code === 'seat_taken') {
      setSeats((current) => current.map((item) => (
        item.seat_number === seat ? { ...item, claimed: true } : item
      )));
      setMessage('Someone just picked that seat. Choose your seat again.');
      setStep('grid');
    } else if (error.code === 'incorrect_secret') {
      const tries = Number(error.remaining_attempts);
      setMessage(Number.isFinite(tries)
        ? `Those pictures did not match. ${tries} ${tries === 1 ? 'try' : 'tries'} left before a short lock.`
        : 'Those pictures did not match. Try again or ask your teacher.');
    } else {
      setMessage(error.detail || 'Check your entry and try again.');
    }
  };

  const lookup = async (nextCode = code) => {
    setBusy(true); setMessage('');
    try {
      const result = await lookupClass(nextCode);
      setProgram(result.program); setSeats(result.seats); setOpenSeats(result.open_seats || 0);
      setCode(classCode(nextCode)); setStep('grid');
      return result;
    } catch (error) {
      showError(error);
      return null;
    } finally { setBusy(false); }
  };

  useEffect(() => {
    let active = true;
    const start = async () => {
      const token = sessionStorage.getItem(SEAT_TOKEN_KEY);
      if (qr.current) {
        sessionStorage.removeItem(SEAT_TOKEN_KEY);
        purgeSeatBackups();
        setBusy(true);
        try {
          const found = await lookupClass(qr.current.code);
          if (!active) return;
          setProgram(found.program); setSeats(found.seats); setOpenSeats(found.open_seats || 0);
          setCode(classCode(qr.current.code));
          setSeat(qr.current.seatNumber);
          const result = await signInWithBackup({
            code: qr.current.code,
            seat_number: qr.current.seatNumber,
            backup_code: qr.current.backupCode,
          });
          if (!active) return;
          if (result.needs_pictures) {
            const resetSeat = found.seats.find((item) => item.seat_number === qr.current.seatNumber);
            if (resetSeat?.needs_pictures) setStep('repick');
            else {
              setSeatToken(result.seat_token);
              setStep(found.program.names_enabled ? 'name' : 'pick');
            }
          } else await openSession(result.seat_token);
        } catch (error) {
          if (active) { setStep('code'); showError(error); }
        } finally { if (active) setBusy(false); }
        return;
      }
      if (token) {
        try {
          await openSession(token);
          return;
        } catch (error) {
          sessionStorage.removeItem(SEAT_TOKEN_KEY);
          purgeSeatBackups();
          if (error.code === 'seat_signed_out') setMessage('Your teacher reset your seat. Sign in again.');
          else showError(error);
        }
      } else purgeSeatBackups();
      if (active) setStep('code');
    };
    start();
    return () => { active = false; };
    // The route owns the initial token/fragment check for this mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chooseSeat = (item) => {
    setSeat(item.seat_number); setSecret([]); setFirstSecret(null); setMessage('');
    setStep(item.needs_pictures ? 'repick' : 'signin');
  };

  const startNew = () => {
    if (!openSeats) {
      setMessage('All seats are taken. Ask your teacher to add a seat.');
      return;
    }
    setSeat(null); setSeatToken(null); setName(''); setSecret([]);
    setFirstSecret(null); setMessage(''); setStep(program.names_enabled ? 'name' : 'pick');
  };

  const continueName = () => {
    const normalized = normalizeSeatDisplayName(name);
    if (!normalized) { setMessage('Use your first name and one or two last letters, like Jaylen Ro.'); return; }
    // Catch a taken name before the kid picks pictures twice. The server still enforces it.
    const key = (value) => String(value || '').normalize('NFKC').toLocaleLowerCase().replace(/\s+/g, ' ').trim();
    if (seats.some((item) => item.display_name && key(item.display_name) === key(normalized))) {
      setMessage('Someone in your class already has that name. Add another letter, like Jaylen Ro.'); return;
    }
    setName(normalized); setMessage(''); setStep('pick');
  };

  const submitPictures = async (picked, mode = step) => {
    if (picked.length !== 3) return;
    if (mode === 'pick') { setFirstSecret(picked); setSecret([]); setMessage(''); setStep('confirm'); return; }
    if (mode === 'confirm' && picked.some((id, index) => id !== firstSecret[index])) {
      setSecret([]); setMessage('Those pictures did not match. Pick them again.'); return;
    }
    setBusy(true); setMessage('');
    try {
      if (mode === 'signin') {
        const result = await signInSeat({ code, seat_number: seat, secret: picked });
        await openSession(result.seat_token);
      } else if (mode === 'repick') {
        const result = await repickSeat({ code, seat_number: seat, secret: picked });
        await openSession(result.seat_token);
      } else if (seatToken) {
        await claimSeatPictures(seatToken, { secret: picked, ...(program.names_enabled ? { display_name: name } : {}) });
        await openSession(seatToken);
      } else {
        const result = await joinNewSeat({ code, secret: picked, ...(program.names_enabled ? { display_name: name } : {}) });
        setSeat(result.seat_number);
        if (result.message) {
          setSeatToken(result.seat_token); setMessage(result.message); setStep('assigned');
        } else await openSession(result.seat_token);
      }
    } catch (error) {
      setSecret([]); showError(error);
    } finally { setBusy(false); }
  };

  const pickPicture = (id) => {
    if (busy || secret.length >= 3) return;
    const next = [...secret, id];
    setSecret(next);
    if (next.length === 3) submitPictures(next);
  };

  if (step === 'boot') return <main className="join-page" data-skin="playground" aria-label="Loading seat"><div className="join-card"><p>Opening your seat...</p></div></main>;

  const listedSeats = [...seats].sort((left, right) => (program?.names_enabled
    ? (left.display_name || `Seat ${left.seat_number}`).localeCompare(right.display_name || `Seat ${right.seat_number}`)
    : left.seat_number - right.seat_number));

  return <main className="join-page" data-skin="playground">
    <div className="join-card">
      <div className="join-brand"><img src="/Pizzul_Hero.png" alt="Pizzul" /><div><strong>Beat Arranger</strong><span>Class seat sign-in</span></div></div>
      {step === 'code' && <form onSubmit={(event) => { event.preventDefault(); lookup(); }}>
        <h1>What is your class code?</h1>
        <label className="join-field">Class code<input autoFocus aria-label="Class code" autoCapitalize="characters" value={code} onChange={(event) => setCode(classCode(event.target.value))} placeholder="ABC-234" /></label>
        <button className="join-primary" disabled={busy || code.replace('-', '').length !== 6}>Let&apos;s go</button>
      </form>}
      {step === 'grid' && <section>
        <button className="join-back" onClick={() => setStep('code')}>Change code</button>
        <h1>{program.name}</h1><p>{program.names_enabled ? 'Tap your name.' : 'Tap your seat number.'}</p>
        <div className="join-name-list">{listedSeats.map((item) => <button key={item.seat_number} onClick={() => chooseSeat(item)} aria-label={program.names_enabled ? item.display_name || `Seat ${item.seat_number}` : `Seat ${item.seat_number}`}>
          <strong>{program.names_enabled ? item.display_name || `Seat ${item.seat_number}` : `Seat ${item.seat_number}`}</strong>
          {item.needs_pictures && <span>Pick new pictures</span>}
        </button>)}</div>
        <button className="join-primary join-new" onClick={startNew}>I&apos;m new</button>
      </section>}
      {step === 'name' && <section>
        <h1>Tell us your name</h1><p>Type your first name and one or two letters from your last name.</p><p className="join-example">Example: Jaylen Ro.</p>
        <label className="join-field">Your name<input aria-label="Your name" autoComplete="off" maxLength={40} value={name} onChange={(event) => { setName(event.target.value); setMessage(''); }} /></label>
        <button className="join-primary" onClick={continueName}>Next</button>
      </section>}
      {['pick', 'confirm', 'signin', 'repick'].includes(step) && <section>
        <h1>{step === 'confirm' ? 'Pick them again to lock it in' : step === 'signin' ? 'Tap your 3 pictures' : step === 'repick' ? 'Pick 3 new pictures' : 'Pick 3 pictures in order. Remember them!'}</h1>
        <PictureGrid selected={secret} onPick={pickPicture} />
      </section>}
      {step === 'assigned' && <section className="join-assigned"><h1>That seat is yours!</h1><button className="join-primary" onClick={() => openSession(seatToken)}>Open my beat</button></section>}
      {message && <p className="join-message" role="alert">{message}</p>}
      {busy && step !== 'code' && <p className="join-busy" role="status">Checking...</p>}
    </div>
  </main>;
}

export default JoinPage;
